import { List } from 'antd';
import { useTheme } from 'styled-components';
import useOrderHistoryController from '../../../1_application/client/clientOrderHistoryController';
import { Client } from '../../../2_domain/client/client';
import { TestSummary } from '../../../2_domain/test/test';
import { getIndex } from '../../../lib/util/pagination';
import BoxContainer from '../../core/components/box/boxContainer';
import Column from '../../core/components/box/column';
import Row from '../../core/components/box/row';
import SizedBox from '../../core/components/box/sizedBox';
import LXMiddleButton from '../../core/components/buttons/lxMiddleButton';
import LXPageSizeSelector from '../../core/components/buttons/lxPageSizeSelector';
import LXSortSelector from '../../core/components/buttons/lxSortSelector';
import LXList from '../../core/components/list/lx_list';
import {
  HeaderPadding,
  ItemPadding,
} from '../../core/components/list/lx_list_padding';
import LXPagination from '../../core/pagination/lxPagination';
import { LXbody, LXCaption, LXh4 } from '../../core/texts/lxText';

interface OrderHistoryProps {
  client: Client;
}
const OrderHistory = (props: OrderHistoryProps) => {
  const theme = useTheme();
  const c = useOrderHistoryController(props.client);

  const pagination = (
    <Row justifyContent="space-between" alignItems="center">
      <Row width="192px">
        <LXMiddleButton onClick={c.onClickRedirectToNewOrder}>
          {theme.test.plusOrder}
        </LXMiddleButton>
      </Row>
      <LXPagination
        current={c.pageResult?.pageNumber ?? 0}
        pageSize={c.pagination.pageSize}
        total={c.pageResult?.totalElements ?? 0}
        onChange={c.setPageNumber}
      ></LXPagination>
      <Row alignItems="center">
        <LXbody color={theme.colors.caption}>{theme.texts.rowsPerPage}</LXbody>
        <SizedBox width={10}></SizedBox>
        <LXPageSizeSelector
          value={c.pagination.pageSize}
          onSelect={c.setPageSize}
        ></LXPageSizeSelector>
      </Row>
    </Row>
  );

  const listHeader = (
    <HeaderPadding padding="0 10px">
      <Row alignItems="center">
        <LXCaption width={'44px'}>{theme.texts.no}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'144px'}>{theme.texts.testNo}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'294px'}>{theme.texts.testInformation}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'184px'}>{theme.texts.testDate}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'144px'}>{theme.texts.status}</LXCaption>
      </Row>
    </HeaderPadding>
  );

  const buildTestName = (test: TestSummary) => {
    if (test.numberOfItems > 1) {
      return (
        <Row>
          <LXbody>{test.name}</LXbody>
          <SizedBox width={2}></SizedBox>
          <LXbody color={theme.colors.caption}>{`+${
            test.numberOfItems - 1
          }`}</LXbody>
        </Row>
      );
    } else {
      return <LXbody>{test.name}</LXbody>;
    }
  };

  const listItemBuilder = (test: TestSummary, idx: number) => (
    <List.Item>
      <ItemPadding onClick={() => c.onClickRedirectToOrderId(test.id)}>
        <Row alignItems="center">
          <LXbody width={'44px'}>
            {getIndex(c.pageResult.pageNumber, c.pagination.pageSize, idx)}
          </LXbody>
          <SizedBox width={16}></SizedBox>
          <LXbody width={'144px'}>{test.serial}</LXbody>
          <SizedBox width={16}></SizedBox>
          <LXbody width={'294px'}>{buildTestName(test)}</LXbody>
          <SizedBox width={16}></SizedBox>
          <LXbody width={'184px'}>{test.clientName}</LXbody>
          <SizedBox width={16}></SizedBox>
          <LXbody width={'144px'}>{test.testStatus.label}</LXbody>
        </Row>
      </ItemPadding>
    </List.Item>
  );

  const OrderHistoryHeader = (
    <Row justifyContent="space-between" alignItems="center">
      <Row alignItems="center">
        <LXh4>{theme.test.orderHistory}</LXh4>
        <SizedBox width={4} />
        <LXCaption>{c.pageResult?.totalElements ?? 0}</LXCaption>
      </Row>
      <LXSortSelector
        value={c.pagination.isAscending ? 'Oldest' : 'Newest'}
        onSelect={c.setSortFilter}
      ></LXSortSelector>
    </Row>
  );

  return (
    <Column>
      {OrderHistoryHeader}
      <SizedBox height={16}></SizedBox>
      <BoxContainer padding="0px">
        <LXList<TestSummary>
          datas={c.tests}
          header={listHeader}
          itemBuilder={listItemBuilder}
          loading={c.isLoading}
        ></LXList>
      </BoxContainer>
      <SizedBox height={16}></SizedBox>
      {pagination}
    </Column>
  );
};

export default OrderHistory;
